import React, { useLayoutEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import BrandLoader from '../Brand/BrandLoader';
import LoadFailure from '../Brand/LoadFailure';
import { loadWithRecovery } from '../../utils/loadWithRecovery';
import { PhotoIcon } from '../Brand/Icon';
import useSessionIdentity, { getSessionIdentity } from '../../hooks/useSessionIdentity';
import { isDeletedOwner } from '../../accountDeletion/ownerState';
import { accountPhotoUrl, readAccountPhoto } from '../../utils/accountPhoto';
import './index.scss';

const pixels = value => {
  const match = String(value ?? '').match(/^\s*(\d+(?:\.\d+)?)(?:px)?\s*$/);
  return match && Number(match[1]) > 0 ? Number(match[1]) : undefined;
};

function ImageRequest({ style, className = '', onLoad, onError, sourcePending = false, onSourceRetry, containerRef, ...props }) {
  const { t } = useTranslation('brand');
  const hasSource = Boolean(props.src || props.srcSet);
  const [status, setStatus] = useState(hasSource || sourcePending ? 'loading' : 'error');
  const [attempt, setAttempt] = useState(0);
  const [cycle, setCycle] = useState(0);
  const settle = useRef(null);
  const image = useRef(null);
  const width = style?.width ?? props.width;
  const height = style?.height ?? props.height;
  const pixelWidth = pixels(width);
  const pixelHeight = pixels(height);
  const sourceWidth = pixels(props.width);
  const sourceHeight = pixels(props.height);
  // A CSS width with height:auto scales the source's ratio; it must not be
  // paired with the unscaled HTML height to reserve a different rectangle.
  const aspectRatio = style?.aspectRatio || (pixels(style?.height)
    ? pixelWidth && pixelHeight && `${pixelWidth} / ${pixelHeight}`
    : sourceWidth && sourceHeight && `${sourceWidth} / ${sourceHeight}`) || undefined;

  useLayoutEffect(() => {
    if (!hasSource) { setStatus(sourcePending ? 'loading' : 'error'); return undefined; }
    const controller = new AbortController();
    setStatus('loading');
    const start = () => {
      if (image.current?.complete && image.current.naturalWidth > 0) { setStatus('loaded'); return; }
      let first = true;
      loadWithRecovery(() => new Promise(resolve => {
        settle.current = resolve;
        // The initial img is already requesting its source. Only replace it
        // for a retry, not on every successful first mount.
        if (!first || cycle) setAttempt(value => value + 1);
        first = false;
      }), { signal: controller.signal }).then(result => {
        if (!controller.signal.aborted) setStatus(result.success ? 'loaded' : 'error');
      });
    };
    // A lazy image outside the viewport is not a stalled network request.
    const observer = props.loading === 'lazy' && !cycle && window.IntersectionObserver
      ? new IntersectionObserver(entries => {
        if (entries.some(entry => entry.isIntersecting)) { observer.disconnect(); start(); }
      }, { rootMargin: '300px' }) : null;
    if (observer && image.current) observer.observe(image.current);
    else start();
    return () => { controller.abort(); observer?.disconnect(); settle.current = null; };
  }, [hasSource, cycle, props.loading, sourcePending]);

  useLayoutEffect(() => {
    // Cached images may finish before the effect attaches; never leave a
    // fully decoded image behind a pending placeholder.
    if (image.current?.complete && image.current.naturalWidth > 0) {
      settle.current?.({ success: true }); setStatus('loaded');
    }
  }, [attempt]);

  const retry = () => {
    setStatus('loading');
    if (onSourceRetry) { onSourceRetry(); return; }
    // Replace the failed request without changing the original URL (which may
    // be signed), its srcSet, dimensions, or its surrounding link.
    setCycle(value => value + 1);
  };

  return <span ref={containerRef} className="content-image" data-state={status} data-sized={Boolean(aspectRatio)}
    style={status === 'loaded' ? undefined : {
      width: pixelWidth || width,
      ...(style?.margin !== undefined ? { margin: style.margin } : {}),
      ...Object.fromEntries(['marginTop', 'marginRight', 'marginBottom', 'marginLeft'].filter(key => style?.[key] !== undefined).map(key => [key, style[key]])),
      ...(aspectRatio ? { aspectRatio } : {}),
    }}>
    {hasSource && <img {...props} alt={props.alt || ''} key={attempt} ref={image}
      className={`content-image__asset ${className}`.trim()}
      style={{ ...style, ...(status !== 'loaded' ? {
        visibility: 'hidden', margin: 0,
        // Percentage widths belong to the comment. The temporary box already
        // applies that percentage, so the image should fill it, not scale twice.
        ...((pixelWidth || String(width).trim().endsWith('%')) ? { width: '100%' } : {}),
      } : {}) }}
      aria-hidden={status !== 'loaded' ? true : props['aria-hidden']}
      onLoad={event => { settle.current?.({ success: true }); setStatus('loaded'); if (typeof onLoad === 'function') onLoad(event); }}
      onError={event => { settle.current?.({ success: false, statusCode: 600 }); if (typeof onError === 'function') onError(event); }} />}
    {status === 'loading' && <span className="content-image__state"><BrandLoader compact label={t('loading')} /></span>}
    {status === 'error' && <span className="content-image__state content-image__error" role="group" aria-label={t('loadContentError')}>
      {hasSource || onSourceRetry ? <LoadFailure onRetry={retry} /> : <PhotoIcon size={28} />}
    </span>}
  </span>;
}

function AccountPhotoRequest({ source, identity, renderImage, ...props }) {
  const [result, setResult] = useState({ url: null, pending: true });
  const [cycle, setCycle] = useState(0);
  const container = useRef(null);
  useLayoutEffect(() => {
    const controller = new AbortController();
    const ownerId = JSON.parse(identity)[0];
    let objectUrl;
    let started = false;
    const release = () => { if (objectUrl) { URL.revokeObjectURL(objectUrl); objectUrl = null; } };
    setResult({ url: null, pending: true });
    const start = () => {
      if (started) return;
      started = true;
      readAccountPhoto(source, { signal: controller.signal, identity }).then(blob => {
        if (controller.signal.aborted || getSessionIdentity() !== identity || isDeletedOwner(ownerId)) return;
        objectUrl = URL.createObjectURL(blob);
        setResult({ url: objectUrl, pending: false });
      }).catch(() => {
        if (!controller.signal.aborted) setResult({ url: null, pending: false });
      });
    };
    const deleted = () => {
      if (!isDeletedOwner(ownerId)) return;
      controller.abort(); release(); setResult({ url: null, pending: false });
    };
    window.addEventListener('wiw:account-deleted', deleted);
    window.addEventListener('storage', deleted);
    const observer = props.loading === 'lazy' && !cycle && window.IntersectionObserver
      ? new IntersectionObserver(entries => {
        if (entries.some(entry => entry.isIntersecting)) { observer.disconnect(); start(); }
      }, { rootMargin: '300px' }) : null;
    if (observer && container.current) observer.observe(container.current);
    else start();
    return () => {
      controller.abort(); observer?.disconnect(); release();
      window.removeEventListener('wiw:account-deleted', deleted);
      window.removeEventListener('storage', deleted);
    };
  }, [source, identity, cycle, props.loading]);
  if (renderImage) return renderImage({ src: result.url, pending: result.pending, containerRef: container });
  return <ImageRequest {...props} key={`${cycle}|${result.url || ''}`} src={result.url || undefined} srcSet={undefined}
    containerRef={container} sourcePending={result.pending} onSourceRetry={() => setCycle(value => value + 1)} />;
}

export function AccountPhoto(props) {
  const identity = useSessionIdentity();
  return <AccountPhotoRequest {...props} key={identity} identity={identity} />;
}

export default function ContentImage(props) {
  const source = accountPhotoUrl(props.src);
  if (source) return <AccountPhoto key={source} {...props} source={source} />;
  return <ImageRequest key={`${props.src || ''}|${props.srcSet || ''}`} {...props} />;
}
